import React, { useRef, useMemo, useContext } from 'react'
import { InputAdornment, TextField, TextFieldProps } from '@mui/material'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import WarningIcon from '@mui/icons-material/Warning'
import { muiColors } from '@zuto/theme/src/mui/DefaultTheme'
import { ApplicationFormContext } from '../../../../../src/components/ApplicationForm/ApplicationFormContext'

export type MuiTextFieldProps = {
  adornmentLabel?: string
  adornmentShown?: boolean
  autoComplete?: string
  autoFocus?: boolean
  darkerInput?: boolean
  defaultValue?: string
  disabled?: boolean
  isError?: boolean
  errorMessage?: string | React.JSX.Element
  helperMessage?: string
  id: string
  inputMaxLength?: number
  inputSize?: string
  inputType: string
  inputMode?: "none" | "search" | "text" | "numeric" | "tel" | "url" | "email" | "decimal",
  inputVariant: 'filled' | 'outlined'
  isSelected?: boolean
  isSelectedHandler?: React.FocusEventHandler<HTMLInputElement | HTMLTextAreaElement>
  label: string
  max?: string
  mb?: string
  mt?: string
  min?: string
  multipleInputs?: boolean
  name?: string
  onBlurHandler?: React.FocusEventHandler<HTMLInputElement | HTMLTextAreaElement>
  onChangeHandler?: React.ChangeEventHandler<HTMLInputElement>
  onFocus?: React.FocusEventHandler<HTMLInputElement>
  onKeyPressHandler?: React.KeyboardEventHandler<HTMLInputElement>
  pattern?: string
  placeholder?: string
  refHandler?: (r: HTMLInputElement) => void
  spacingRight?: boolean
  stepValue?: string
  testId?: string
  value?: string | number
}

const MuiTextField = (
  {
    adornmentLabel,
    adornmentShown,
    autoComplete,
    autoFocus,
    darkerInput,
    defaultValue,
    disabled,
    isError = false,
    errorMessage,
    helperMessage,
    id,
    inputMaxLength,
    inputSize,
    inputType,
    inputMode,
    inputVariant,
    isSelected,
    isSelectedHandler,
    label,
    max,
    mb,
    mt,
    min,
    multipleInputs,
    name,
    onBlurHandler,
    onChangeHandler,
    onKeyPressHandler,
    pattern,
    placeholder,
    refHandler,
    spacingRight,
    stepValue,
    testId,
    value,
    ...props
  }: MuiTextFieldProps
): JSX.Element => {
  const { featureSwitches } = useContext(ApplicationFormContext)
  const {
    inputIconsV1,
    reviewPageV3
  } = featureSwitches ?? {}
  const textRef = useRef<TextFieldProps>()
  const shouldRenderAsError: boolean = useMemo(() => !!isError || !!errorMessage, [errorMessage, isError, helperMessage])

  const adornmentNode = adornmentShown || (adornmentLabel && (isSelected || textRef.current?.value))
    ? <InputAdornment position="start">{adornmentLabel}</InputAdornment>
    : null

  const endAdornmentNode = (inputIconsV1 || reviewPageV3) && (
    (typeof textRef.current?.value === 'string' && textRef.current?.value.length > 0) || shouldRenderAsError
  ) ? (
    <InputAdornment position="end">
      {shouldRenderAsError ? (
        <WarningIcon
          aria-label="invalid input"
          sx={{ color: muiColors.primary.errorRedMain }}
          data-testid={`input-warning-icon-${id}`}
        />
      ) : (
        <CheckCircleIcon
          aria-label="valid input"
          sx={{ color: muiColors.primary.successGreen }}
          data-testid={`input-tick-icon-${id}`}
        />
      )}
    </InputAdornment>
  ) : null

  return (
    <TextField
      className={[
        inputVariant,
        inputSize,
        spacingRight ? 'spacing-right' : '',
        disabled ? 'disabled' : '',
        darkerInput ? 'darker-input' : '',
        multipleInputs ? 'multiple-inputs' : ''
      ].join(' ')}
      defaultValue={defaultValue}
      error={shouldRenderAsError}
      helperText={shouldRenderAsError ? errorMessage : helperMessage}
      id={id}
      inputRef={textRef}
      label={label}
      onBlur={onBlurHandler}
      onChange={onChangeHandler}
      onFocus={isSelectedHandler}
      onKeyPress={onKeyPressHandler}
      style={{ marginBottom: mb, marginTop: mt }}
      variant={inputVariant}
      inputProps={{
        'data-testid': testId,
        autoComplete,
        autoFocus,
        disabled,
        max,
        maxLength: inputMaxLength,
        min,
        name,
        pattern,
        placeholder,
        ref: refHandler,
        step: stepValue,
        ...props
      }}
      InputProps={{
        startAdornment: !disabled && adornmentNode,
        endAdornment: endAdornmentNode,
        type: inputType,
        inputMode: inputMode,
        value
      }}
    />
  )
}

export default MuiTextField
